import React, { useState, useMemo, useCallback } from 'react';
import ShowcasePageWrapper from '../shared/ShowcasePageWrapper';
import { generateKanbanData } from '../shared/SampleData';
import Button from '@salesforce/design-system-react/components/button';
import Badge from '@salesforce/design-system-react/components/badge';
import Icon from '@salesforce/design-system-react/components/icon';
import Avatar from '@salesforce/design-system-react/components/avatar';
import Modal from '@salesforce/design-system-react/components/modal';
import Toast from '@salesforce/design-system-react/components/toast';
import ToastContainer from '@salesforce/design-system-react/components/toast/container';
import Combobox from '@salesforce/design-system-react/components/combobox';
import Input from '@salesforce/design-system-react/components/input';

// --- Style constants ---

const styles = {
  toolbarContainer: {
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '4px',
    padding: '16px 20px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  summaryBar: {
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '4px',
    padding: '12px 20px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: '11px',
    color: '#706e6b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '2px',
  },
  summaryValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#080707',
  },
  boardContainer: {
    display: 'flex',
    gap: '16px',
    overflowX: 'auto',
    paddingBottom: '8px',
    minHeight: '500px',
  },
  column: {
    flex: '1 1 0',
    minWidth: '240px',
    maxWidth: '320px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f3f3f3',
    borderRadius: '4px',
    border: '1px solid #dddbda',
    overflow: 'hidden',
  },
  columnHighlight: {
    borderColor: '#0176d3',
    borderWidth: '2px',
    boxShadow: '0 0 0 1px #0176d3',
    backgroundColor: '#eef4ff',
  },
  columnHeader: {
    padding: '12px 16px',
    backgroundColor: 'white',
    borderBottom: '1px solid #dddbda',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  columnTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  columnName: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#080707',
    margin: 0,
  },
  columnTotal: {
    fontSize: '12px',
    color: '#706e6b',
    margin: 0,
    fontWeight: '400',
  },
  columnBody: {
    padding: '8px',
    flex: 1,
    overflowY: 'auto',
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  card: {
    backgroundColor: 'white',
    border: '1px solid #dddbda',
    borderRadius: '4px',
    padding: '12px',
    cursor: 'grab',
    transition: 'box-shadow 0.15s, opacity 0.15s',
    position: 'relative',
  },
  cardDragging: {
    opacity: 0.5,
  },
  cardHover: {
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  },
  cardName: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#080707',
    marginBottom: '4px',
    lineHeight: '1.3',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardAccount: {
    fontSize: '12px',
    color: '#706e6b',
    marginBottom: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardAmount: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#0176d3',
    marginBottom: '8px',
  },
  cardOwnerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
  },
  cardOwnerName: {
    fontSize: '12px',
    color: '#3e3e3c',
  },
  cardMetaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '6px',
  },
  cardDate: {
    fontSize: '11px',
    color: '#706e6b',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  cardPriorityDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  cardDaysInStage: {
    fontSize: '11px',
    color: '#706e6b',
    marginTop: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  detailSection: {
    marginBottom: '20px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#706e6b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#080707',
    fontWeight: '400',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '20px',
  },
  emptyColumn: {
    padding: '20px',
    textAlign: 'center',
    color: '#706e6b',
    fontSize: '13px',
    fontStyle: 'italic',
  },
};

// --- Utility helpers ---

const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatCurrencyFull = (value) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


const getProbabilityColor = (probability) => {
  if (probability > 70) return '#2e844a';
  if (probability >= 40) return '#fe9339';
  return '#c23934';
};

const getProbabilityLabel = (probability) => {
  if (probability > 70) return 'High';
  if (probability >= 40) return 'Medium';
  return 'Low';
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'Critical':
      return '#c23934';
    case 'High':
      return '#fe9339';
    case 'Medium':
      return '#0176d3';
    case 'Low':
      return '#2e844a';
    default:
      return '#706e6b';
  }
};

const getColumnColor = (index) => {
  const columnColors = ['#9050e9', '#0176d3', '#fe9339', '#06a59a', '#2e844a'];
  return columnColors[index % columnColors.length];
};

const getColumnTotal = (cards) =>
  cards.reduce((sum, card) => sum + card.amount, 0);

const getOwnerOptions = (columns) => {
  const owners = new Set();
  columns.forEach((col) =>
    col.cards.forEach((card) => owners.add(card.owner))
  );
  return [{ id: 'all', label: 'All Owners' }].concat(
    Array.from(owners)
      .sort()
      .map((name) => ({ id: name, label: name }))
  );
};

// --- Sub-components ---

const ProbabilityBadge = ({ probability }) => {
  const color = getProbabilityColor(probability);
  const label = getProbabilityLabel(probability);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: color,
        lineHeight: '16px',
      }}
    >
      {probability}% {label}
    </span>
  );
};

const KanbanCard = ({
  card,
  columnId,
  isDragging,
  onDragStart,
  onDragEnd,
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);

  const cardStyle = {
    ...styles.card,
    ...(isDragging ? styles.cardDragging : {}),
    ...(hovered && !isDragging ? styles.cardHover : {}),
    borderLeft: `3px solid ${getPriorityColor(card.priority)}`,
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, card, columnId)}
      onDragEnd={onDragEnd}
      onClick={() => onClick(card, columnId)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={cardStyle}
      title={`Drag to move "${card.name}" to another stage`}
    >
      <div style={styles.cardName} title={card.name}>
        {card.name}
      </div>
      <div style={styles.cardAccount} title={card.account}>
        {card.account}
      </div>
      <div style={styles.cardAmount}>{formatCurrencyFull(card.amount)}</div>
      <div style={styles.cardOwnerRow}>
        <Avatar
          variant="user"
          label={card.owner}
          size="x-small"
          title={card.owner}
        />
        <span style={styles.cardOwnerName}>{card.owner}</span>
      </div>
      <div style={styles.cardMetaRow}>
        <span style={styles.cardDate}>
          <Icon
            category="utility"
            name="event"
            size="xx-small"
            style={{ fill: '#706e6b' }}
          />
          {card.closeDate}
        </span>
        <ProbabilityBadge probability={card.probability} />
      </div>
      <div style={styles.cardDaysInStage}>
        <span
          style={{
            ...styles.cardPriorityDot,
            backgroundColor: getPriorityColor(card.priority),
          }}
        />
        {card.daysInStage} {card.daysInStage === 1 ? 'day' : 'days'} in stage
        &middot; {card.priority}
      </div>
    </div>
  );
};

const ColumnHeader = ({ column, index, cardCount }) => {
  const total = getColumnTotal(column.cards);
  const color = getColumnColor(index);

  return (
    <div style={styles.columnHeader}>
      <div style={styles.columnTitle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '2px',
              backgroundColor: color,
              display: 'inline-block',
            }}
          />
          <h3 style={styles.columnName}>{column.name}</h3>
        </div>
        <Badge
          content={String(cardCount)}
          style={{
            backgroundColor: color,
            color: 'white',
            borderColor: color,
          }}
        />
      </div>
      <div style={styles.columnTotal}>
        Total: {formatCurrency(total)}
      </div>
    </div>
  );
};

const CardDetailModal = ({
  card,
  columnName,
  isOpen,
  onClose,
  onDelete,
}) => {
  const [note, setNote] = useState('');
  const [notes, setNotes] = useState([]);
  const [showNoteInput, setShowNoteInput] = useState(false);

  const handleAddNote = () => {
    if (note.trim()) {
      setNotes((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: note.trim(),
          timestamp: new Date().toLocaleString(),
        },
      ]);
      setNote('');
      setShowNoteInput(false);
    }
  };

  if (!card) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      heading={card.name}
      size="medium"
      footer={[
        <Button key="cancel" label="Close" onClick={onClose} />,
        <Button
          key="delete"
          label="Delete"
          variant="destructive"
          onClick={() => onDelete(card)}
        />,
        <Button
          key="edit"
          label="Edit Deal"
          variant="brand"
          onClick={onClose}
        />,
      ]}
    >
      <div style={{ padding: '20px' }}>
        {/* Stage indicator */}
        <div
          style={{
            backgroundColor: '#eef4ff',
            border: '1px solid #d8edff',
            borderRadius: '4px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Icon
            category="utility"
            name="stage"
            size="x-small"
            style={{ fill: '#0176d3' }}
          />
          <span style={{ fontSize: '13px', color: '#0176d3', fontWeight: '600' }}>
            Current Stage: {columnName}
          </span>
        </div>

        {/* Detail grid */}
        <div style={styles.detailGrid}>
          <div style={styles.detailSection}>
            <div style={styles.detailLabel}>Deal Name</div>
            <div style={{ ...styles.detailValue, fontWeight: '700' }}>
              {card.name}
            </div>
          </div>
          <div style={styles.detailSection}>
            <div style={styles.detailLabel}>Account</div>
            <div style={styles.detailValue}>{card.account}</div>
          </div>
          <div style={styles.detailSection}>
            <div style={styles.detailLabel}>Amount</div>
            <div
              style={{
                ...styles.detailValue,
                fontWeight: '700',
                color: '#0176d3',
                fontSize: '18px',
              }}
            >
              {formatCurrencyFull(card.amount)}
            </div>
          </div>
          <div style={styles.detailSection}>
            <div style={styles.detailLabel}>Close Date</div>
            <div style={styles.detailValue}>{card.closeDate}</div>
          </div>
          <div style={styles.detailSection}>
            <div style={styles.detailLabel}>Probability</div>
            <div>
              <ProbabilityBadge probability={card.probability} />
            </div>
          </div>
          <div style={styles.detailSection}>
            <div style={styles.detailLabel}>Priority</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span
                style={{
                  ...styles.cardPriorityDot,
                  backgroundColor: getPriorityColor(card.priority),
                }}
              />
              <span style={styles.detailValue}>{card.priority}</span>
            </div>
          </div>
          <div style={styles.detailSection}>
            <div style={styles.detailLabel}>Owner</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Avatar
                variant="user"
                label={card.owner}
                size="small"
                title={card.owner}
              />
              <span style={styles.detailValue}>{card.owner}</span>
            </div>
          </div>
          <div style={styles.detailSection}>
            <div style={styles.detailLabel}>Days in Stage</div>
            <div style={styles.detailValue}>
              {card.daysInStage} {card.daysInStage === 1 ? 'day' : 'days'}
            </div>
          </div>
        </div>

        {/* Probability meter */}
        <div style={{ marginBottom: '20px' }}>
          <div style={styles.detailLabel}>Win Probability</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginTop: '8px',
            }}
          >
            <div
              style={{
                flex: 1,
                height: '8px',
                backgroundColor: '#dddbda',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${card.probability}%`,
                  height: '100%',
                  backgroundColor: getProbabilityColor(card.probability),
                  borderRadius: '4px',
                  transition: 'width 0.3s',
                }}
              />
            </div>
            <span
              style={{
                fontSize: '14px',
                fontWeight: '700',
                color: getProbabilityColor(card.probability),
                minWidth: '40px',
              }}
            >
              {card.probability}%
            </span>
          </div>
        </div>

        {/* Weighted value */}
        <div
          style={{
            backgroundColor: '#f3f3f3',
            borderRadius: '4px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: '13px', color: '#706e6b' }}>
            Weighted Value
          </span>
          <span style={{ fontSize: '16px', fontWeight: '700', color: '#080707' }}>
            {formatCurrencyFull(card.amount * (card.probability / 100))}
          </span>
        </div>

        {/* Notes section */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
            }}
          >
            <span
              style={{
                fontSize: '14px',
                fontWeight: '700',
                color: '#080707',
              }}
            >
              Notes ({notes.length})
            </span>
            <Button
              label="Add Note"
              variant="outline-brand"
              iconCategory="utility"
              iconName="add"
              iconPosition="left"
              onClick={() => setShowNoteInput(true)}
            />
          </div>

          {showNoteInput && (
            <div
              style={{
                border: '1px solid #dddbda',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '12px',
                backgroundColor: '#fafaf9',
              }}
            >
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note about this deal..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  border: '1px solid #dddbda',
                  borderRadius: '4px',
                  padding: '8px',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  marginBottom: '8px',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Button
                  label="Cancel"
                  onClick={() => {
                    setShowNoteInput(false);
                    setNote('');
                  }}
                />
                <Button
                  label="Save Note"
                  variant="brand"
                  onClick={handleAddNote}
                  disabled={!note.trim()}
                />
              </div>
            </div>
          )}

          {notes.length === 0 && !showNoteInput && (
            <div
              style={{
                padding: '16px',
                textAlign: 'center',
                color: '#706e6b',
                fontSize: '13px',
                border: '1px dashed #dddbda',
                borderRadius: '4px',
              }}
            >
              No notes yet. Click "Add Note" to add one.
            </div>
          )}

          {notes.map((n) => (
            <div
              key={n.id}
              style={{
                border: '1px solid #dddbda',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '8px',
                backgroundColor: 'white',
              }}
            >
              <div
                style={{
                  fontSize: '13px',
                  color: '#080707',
                  marginBottom: '4px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {n.text}
              </div>
              <div style={{ fontSize: '11px', color: '#706e6b' }}>
                {n.timestamp}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

const EmptyColumnDrop = () => (
  <div style={styles.emptyColumn}>
    <Icon
      category="utility"
      name="drag_and_drop"
      size="small"
      style={{ fill: '#dddbda', marginBottom: '8px' }}
    />
    <div>Drop cards here</div>
  </div>
);

// --- Main component ---

const KanbanBoard = () => {
  // Initialize data with useMemo, then store in useState for mutations
  const initialData = useMemo(() => generateKanbanData(), []);
  const [columns, setColumns] = useState(initialData);

  // Drag-and-drop state
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Detail modal state
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedColumnName, setSelectedColumnName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Toast state
  const [toasts, setToasts] = useState([]);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOwner, setSelectedOwner] = useState([
    { id: 'all', label: 'All Owners' },
  ]);
  const [viewMode, setViewMode] = useState('board'); // 'board' or 'compact'

  // Build owner options from current data
  const ownerOptions = useMemo(() => getOwnerOptions(columns), [columns]);

  // Determine the active owner filter
  const activeOwnerFilter = useMemo(() => {
    if (
      selectedOwner.length === 0 ||
      (selectedOwner.length === 1 && selectedOwner[0].id === 'all')
    ) {
      return null;
    }
    return selectedOwner[0].label;
  }, [selectedOwner]);

  // Compute filtered columns
  const filteredColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      cards: col.cards.filter((card) => {
        const matchesSearch =
          !searchTerm ||
          card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          card.account.toLowerCase().includes(searchTerm.toLowerCase()) ||
          card.owner.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesOwner =
          !activeOwnerFilter || card.owner === activeOwnerFilter;
        return matchesSearch && matchesOwner;
      }),
    }));
  }, [columns, searchTerm, activeOwnerFilter]);

  // Compute totals for summary bar
  const summaryStats = useMemo(() => {
    const allCards = filteredColumns.flatMap((col) => col.cards);
    const totalCards = allCards.length;
    const totalPipeline = allCards.reduce((sum, c) => sum + c.amount, 0);
    const avgProbability =
      totalCards > 0
        ? Math.round(
            allCards.reduce((sum, c) => sum + c.probability, 0) / totalCards
          )
        : 0;
    const weightedPipeline = allCards.reduce(
      (sum, c) => sum + c.amount * (c.probability / 100),
      0
    );
    return { totalCards, totalPipeline, avgProbability, weightedPipeline };
  }, [filteredColumns]);

  // --- Toast helpers ---

  const addToast = useCallback((message, variant = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, variant }]);
    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // --- Drag-and-drop handlers ---

  const handleDragStart = useCallback((e, card, columnId) => {
    setDraggedCard({ card, sourceColumn: columnId });
    e.dataTransfer.effectAllowed = 'move';
    // Set a transparent drag image for better UX
    if (e.dataTransfer.setDragImage) {
      const ghost = e.currentTarget.cloneNode(true);
      ghost.style.width = `${e.currentTarget.offsetWidth}px`;
      ghost.style.opacity = '0.8';
      ghost.style.position = 'absolute';
      ghost.style.top = '-9999px';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 20, 20);
      setTimeout(() => document.body.removeChild(ghost), 0);
    }
  }, []);

  const handleDragOver = useCallback((e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  }, []);

  const handleDragLeave = useCallback((e) => {
    // Only reset if leaving the column container itself
    const relatedTarget = e.relatedTarget;
    if (!e.currentTarget.contains(relatedTarget)) {
      setDragOverColumn(null);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedCard(null);
    setDragOverColumn(null);
  }, []);

  const handleDrop = useCallback(
    (e, targetColumnId) => {
      e.preventDefault();
      setDragOverColumn(null);

      if (!draggedCard || draggedCard.sourceColumn === targetColumnId) {
        setDraggedCard(null);
        return;
      }

      const { card, sourceColumn } = draggedCard;
      const targetColumn = columns.find((col) => col.id === targetColumnId);
      const targetName = targetColumn ? targetColumn.name : targetColumnId;

      setColumns((prev) =>
        prev.map((col) => {
          if (col.id === sourceColumn) {
            return {
              ...col,
              cards: col.cards.filter((c) => c.id !== card.id),
            };
          }
          if (col.id === targetColumnId) {
            return {
              ...col,
              cards: [...col.cards, { ...card }],
            };
          }
          return col;
        })
      );

      addToast(`Moved "${card.name}" to ${targetName}`);
      setDraggedCard(null);
    },
    [draggedCard, columns, addToast]
  );

  // --- Card interaction handlers ---

  const handleCardClick = useCallback(
    (card, columnId) => {
      const column = columns.find((col) => col.id === columnId);
      setSelectedCard(card);
      setSelectedColumnName(column ? column.name : '');
      setIsModalOpen(true);
    },
    [columns]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCard(null);
    setSelectedColumnName('');
  }, []);

  const handleDeleteCard = useCallback(
    (card) => {
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          cards: col.cards.filter((c) => c.id !== card.id),
        }))
      );
      addToast(`Deleted "${card.name}"`, 'warning');
      handleCloseModal();
    },
    [addToast, handleCloseModal]
  );

  // --- Filter handlers ---

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleOwnerSelect = useCallback((e, data) => {
    if (data.selection && data.selection.length > 0) {
      setSelectedOwner(data.selection);
    } else {
      setSelectedOwner([{ id: 'all', label: 'All Owners' }]);
    }
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedOwner([{ id: 'all', label: 'All Owners' }]);
  }, []);

  const hasActiveFilters = searchTerm || activeOwnerFilter;

  return (
    <ShowcasePageWrapper
      title="Kanban Board"
      description="Drag-and-drop pipeline management"
      icon="kanban"
      iconCategory="standard"
    >
      {/* Toast container */}
      {toasts.length > 0 && (
        <ToastContainer>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              labels={{ heading: toast.message }}
              variant={toast.variant}
              duration={4000}
              onRequestClose={() => removeToast(toast.id)}
            />
          ))}
        </ToastContainer>
      )}

      {/* Toolbar */}
      <div style={styles.toolbarContainer}>
        <div style={styles.toolbarLeft}>
          <div style={{ minWidth: '220px' }}>
            <Combobox
              id="owner-filter"
              labels={{ placeholder: 'Filter by Owner' }}
              options={ownerOptions}
              selection={selectedOwner}
              variant="readonly"
              events={{
                onSelect: handleOwnerSelect,
              }}
            />
          </div>
          <div style={{ minWidth: '240px' }}>
            <Input
              id="search-deals"
              placeholder="Search deals, accounts, owners..."
              value={searchTerm}
              onChange={handleSearchChange}
              iconLeft={
                <Icon
                  category="utility"
                  name="search"
                  size="x-small"
                  style={{ fill: '#706e6b' }}
                />
              }
            />
          </div>
          {hasActiveFilters && (
            <Button
              label="Clear Filters"
              variant="outline-brand"
              iconCategory="utility"
              iconName="clear"
              iconPosition="left"
              onClick={handleClearFilters}
            />
          )}
        </div>
        <div style={styles.toolbarRight}>
          <Button
            assistiveText={{ icon: 'Board View' }}
            iconCategory="utility"
            iconName="kanban"
            variant={viewMode === 'board' ? 'brand' : 'icon-border'}
            onClick={() => setViewMode('board')}
            title="Board View"
          />
          <Button
            assistiveText={{ icon: 'Compact View' }}
            iconCategory="utility"
            iconName="rows"
            variant={viewMode === 'compact' ? 'brand' : 'icon-border'}
            onClick={() => setViewMode('compact')}
            title="Compact View"
          />
          <span
            style={{
              width: '1px',
              height: '20px',
              backgroundColor: '#dddbda',
              display: 'inline-block',
              margin: '0 4px',
            }}
          />
          <Button
            assistiveText={{ icon: 'Refresh' }}
            iconCategory="utility"
            iconName="refresh"
            variant="icon-border"
            onClick={() => {
              const freshData = generateKanbanData();
              setColumns(freshData);
              addToast('Board refreshed with new data', 'info');
            }}
            title="Refresh Data"
          />
        </div>
      </div>

      {/* Summary bar */}
      <div style={styles.summaryBar}>
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>Total Deals</span>
          <span style={styles.summaryValue}>{summaryStats.totalCards}</span>
        </div>
        <div
          style={{
            width: '1px',
            height: '36px',
            backgroundColor: '#dddbda',
          }}
        />
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>Pipeline Value</span>
          <span style={{ ...styles.summaryValue, color: '#0176d3' }}>
            {formatCurrency(summaryStats.totalPipeline)}
          </span>
        </div>
        <div
          style={{
            width: '1px',
            height: '36px',
            backgroundColor: '#dddbda',
          }}
        />
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>Weighted Pipeline</span>
          <span style={{ ...styles.summaryValue, color: '#2e844a' }}>
            {formatCurrency(summaryStats.weightedPipeline)}
          </span>
        </div>
        <div
          style={{
            width: '1px',
            height: '36px',
            backgroundColor: '#dddbda',
          }}
        />
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>Avg Probability</span>
          <span style={styles.summaryValue}>{summaryStats.avgProbability}%</span>
        </div>
        <div
          style={{
            width: '1px',
            height: '36px',
            backgroundColor: '#dddbda',
          }}
        />
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>Stages</span>
          <span style={styles.summaryValue}>{filteredColumns.length}</span>
        </div>
      </div>

      {/* Board */}
      {viewMode === 'board' ? (
        <div style={styles.boardContainer}>
          {filteredColumns.map((column, colIndex) => {
            const isDropTarget = dragOverColumn === column.id;
            const columnStyle = {
              ...styles.column,
              ...(isDropTarget ? styles.columnHighlight : {}),
            };

            return (
              <div
                key={column.id}
                style={columnStyle}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                <ColumnHeader
                  column={column}
                  index={colIndex}
                  cardCount={column.cards.length}
                />
                <div style={styles.columnBody}>
                  {column.cards.length === 0 ? (
                    <EmptyColumnDrop />
                  ) : (
                    column.cards.map((card) => (
                      <KanbanCard
                        key={card.id}
                        card={card}
                        columnId={column.id}
                        isDragging={
                          draggedCard !== null &&
                          draggedCard.card.id === card.id
                        }
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onClick={handleCardClick}
                      />
                    ))
                  )}
                </div>
                {/* Column footer with total */}
                <div
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    borderTop: '1px solid #dddbda',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#706e6b',
                      textTransform: 'uppercase',
                    }}
                  >
                    {column.cards.length}{' '}
                    {column.cards.length === 1 ? 'deal' : 'deals'}
                  </span>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: getColumnColor(colIndex),
                    }}
                  >
                    {formatCurrency(getColumnTotal(column.cards))}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Compact view */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredColumns.map((column, colIndex) => (
            <div
              key={column.id}
              style={{
                backgroundColor: 'white',
                border: '1px solid #dddbda',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              {/* Compact column header */}
              <div
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#fafaf9',
                  borderBottom: '1px solid #dddbda',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '3px',
                      backgroundColor: getColumnColor(colIndex),
                      display: 'inline-block',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: '#080707',
                    }}
                  >
                    {column.name}
                  </span>
                  <Badge
                    content={String(column.cards.length)}
                    style={{
                      backgroundColor: getColumnColor(colIndex),
                      color: 'white',
                      borderColor: getColumnColor(colIndex),
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: getColumnColor(colIndex),
                  }}
                >
                  {formatCurrency(getColumnTotal(column.cards))}
                </span>
              </div>

              {/* Compact rows */}
              {column.cards.length === 0 ? (
                <div
                  style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    color: '#706e6b',
                    fontSize: '13px',
                  }}
                >
                  No deals in this stage
                </div>
              ) : (
                column.cards.map((card, cardIdx) => (
                  <div
                    key={card.id}
                    onClick={() => handleCardClick(card, column.id)}
                    style={{
                      padding: '10px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      borderBottom:
                        cardIdx < column.cards.length - 1
                          ? '1px solid #f3f3f3'
                          : 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fafaf9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span
                      style={{
                        ...styles.cardPriorityDot,
                        backgroundColor: getPriorityColor(card.priority),
                        flexShrink: 0,
                      }}
                    />
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#080707',
                        }}
                      >
                        {card.name}
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#706e6b',
                          marginLeft: '8px',
                        }}
                      >
                        {card.account}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexShrink: 0,
                      }}
                    >
                      <Avatar
                        variant="user"
                        label={card.owner}
                        size="x-small"
                        title={card.owner}
                      />
                      <span
                        style={{
                          fontSize: '13px',
                          color: '#706e6b',
                          minWidth: '80px',
                        }}
                      >
                        {card.closeDate}
                      </span>
                      <ProbabilityBadge probability={card.probability} />
                      <span
                        style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#0176d3',
                          minWidth: '90px',
                          textAlign: 'right',
                        }}
                      >
                        {formatCurrencyFull(card.amount)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <CardDetailModal
        card={selectedCard}
        columnName={selectedColumnName}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onDelete={handleDeleteCard}
      />
    </ShowcasePageWrapper>
  );
};

export default KanbanBoard;
